<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import {
    effectiveOnlineMaxParty,
    maxBookableOnlineParty,
  } from '@/domain/reservations/reservation-capacity';
  import { shiftIntervalSummary } from '@/domain/reservations/reservation-shifts';
  import type { IBranch } from '@/interfaces/IBranch';
  import type { IReservationSettingsConfiguration } from '@/interfaces/IReservationSettings';
  import { guideWeekdaysLabel } from './reservation-guide-copy';

  /** Finish screen rows Turnos / Mesas / Avisos, each with «Cambiar» back to its step. */
  const props = defineProps<{
    configuration: IReservationSettingsConfiguration;
    branch?: IBranch;
    /** A write is in flight: «Cambiar» waits. */
    disabled: boolean;
  }>();
  const emit = defineEmits<{
    (e: 'go', screen: 'hours' | 'tables' | 'alerts'): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const tc = (key: string, count: number, params?: Record<string, unknown>) =>
    String(proxy.$tc(key, count, params));
  const SEPARATOR = ' · ';

  const hoursText = computed(() => {
    const shifts = [...props.configuration.shifts].sort(
      (a, b) => a.sort - b.sort || a.id - b.id
    );
    const days = new Set<number>();
    for (const shift of shifts) for (const day of shift.days) days.add(day);
    // The interval is stated only when every turno steps the same grid.
    const interval = shiftIntervalSummary(shifts);
    return [
      shifts.map((shift) => shift.name).join(', '),
      guideWeekdaysLabel([...days], proxy.$i18n.locale, tr),
      interval.kind === 'uniform'
        ? tr('reservations.guide.summary.interval', {
            minutes: interval.minutes,
          })
        : '',
    ]
      .filter(Boolean)
      .join(SEPARATOR);
  });
  const tablesText = computed(() => {
    const { settings, tables, tableGroups } = props.configuration;
    const online = tables.filter(
      (table) =>
        table.enabled &&
        table.reservationsMode === 'online' &&
        table.capacity > 0
    ).length;
    const max = effectiveOnlineMaxParty(
      settings.minPartyOnline,
      settings.maxPartyOnline,
      maxBookableOnlineParty(tables, tableGroups)
    );
    return [
      tc('reservations.guide.summary.online', online, { count: online }),
      tc('reservations.guide.summary.combinations', tableGroups.length, {
        count: tableGroups.length,
      }),
      // A maximum means nothing while no table can be booked.
      ...(online > 0 ? [tr('reservations.guide.summary.upTo', { max })] : []),
    ].join(SEPARATOR);
  });
  const alertsText = computed(() => {
    const email = props.configuration.settings.staffEmail;
    const whatsapp = props.branch?.whatsapp?.trim();
    return [
      email || tr('reservations.guide.summary.noEmail'),
      whatsapp
        ? tr('reservations.guide.summary.whatsapp', { number: whatsapp })
        : tr('reservations.guide.summary.noWhatsapp'),
    ].join(SEPARATOR);
  });
  const rows = computed(() => [
    {
      screen: 'hours' as const,
      label: tr('reservations.guide.summary.hours'),
      text: hoursText.value,
    },
    {
      screen: 'tables' as const,
      label: tr('reservations.guide.summary.tables'),
      text: tablesText.value,
    },
    {
      screen: 'alerts' as const,
      label: tr('reservations.guide.summary.alerts'),
      text: alertsText.value,
    },
  ]);
</script>

<template>
  <div class="reservations-guide-summary">
    <div v-for="row in rows" :key="row.screen" class="summary-row">
      <div class="row-text">
        <span class="row-label">{{ row.label }}</span>
        <span class="row-value">{{ row.text }}</span>
      </div>
      <PosUiButton
        color="neutral"
        variant="ghost"
        :aria-label="`${$t('reservations.guide.change')} · ${row.label}`"
        :disabled="disabled"
        @click="emit('go', row.screen)"
        >{{ $t('reservations.guide.change') }}</PosUiButton
      >
    </div>
  </div>
</template>

<style scoped lang="scss">
  .reservations-guide-summary {
    .summary-row {
      @apply tw-flex tw-items-start tw-justify-between tw-gap-3 tw-px-4 tw-py-3;
      & + .summary-row {
        @apply tw-border-0 tw-border-t tw-border-solid tw-border-neutral-200;
      }
    }
    .row-text {
      @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5 tw-pt-2.5;
    }
    .row-label {
      @apply tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-neutral-500;
    }
    .row-value {
      @apply tw-break-words tw-text-sm tw-text-neutral-900;
    }
  }
</style>
